import { useEffect, useState, useRef } from "react";
import { Form, Button } from "react-bootstrap";
import { useGetQuestionnaire, useGetSurvey } from "../api";
import "../style/style.css";
import {
  useUpdateSurveyMutation,
  V1SurveyResource,
  useCalculateSurveyRecommendationsMutation,
  UpdateSurveyApiResponse,
  CalculateSurveyRecommendationsApiResponse,
  useGetProjectByIdQuery,
  useEvaluateQuestionnaireMutation,
} from "@generated";
import { useSearchParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { Survey } from "../types";
import useOauth from "@utils/customHooks/useOauth";
import { Service } from "../../Projects/ServicesV2/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/pro-solid-svg-icons";
import { renderResourceOptions } from "@utils/helperFunctions";
import {
  useListResourceRates,
  useListProjectResources,
  useListResources,
} from "../../Projects/ServicesV2/api";
import Select from "react-select";
import useGetAccount from "../../Account/EditAccount/api/useGetAccount";
import { Section, Question } from "../types";
import { ServicePreview } from "@components/PreviewSlideouts";
import newSurvey from "../../../pages/Projects/CreateProjectMspa/Services/Questionnaire/api/newSurvey";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import QuestionnaireForm from "@components/TakeSurvey/QuestionnaireForm/QuestionnaireForm";
import SurveyRecommendations from "@components/TakeSurvey/SurveyRecommendations";
import { applySurvey as handleApply, formatRecommendations } from "../common";
import useFlags from "@common/hooks/useFlags";

const New = ({ project_id }) => {
  const [authorizationCode] = useOauth();
  const { releaseSurveyAdaptiveQuestions } = useFlags();

  const appHost = process.env.REACT_APP_DOORKEEPER_APP_URL;
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const location = useLocation();
  //boolean as to whether user came from project > surveys OR project > PS services
  const fromProjectServices = location?.state?.from?.includes("take");

  const [searchParams] = useSearchParams();
  const questionnaireId = searchParams.get("questionnaire");
  const [surveyId, setSurveyId] = useState<number | undefined>(undefined);

  const [questions, setQuestions] = useState<Question[] | null>(null);
  const [sections, setSections] = useState<Section[] | null>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const newSectionRef = useRef<HTMLDivElement | null>(null);

  const [recommendations, setRecommendations] =
    useState<Survey["recommendations"] | null>(null);
  const [nonServiceRecommendations, setNonServiceRecommendations] = useState<
    Survey["recommendations"]
  >([]);

  const [step, setStep] = useState<1 | 2>(1);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);
  const [serviceToPreview, setServiceToPreview] = useState<Service>({
    id: "0",
    name: "",
  });
  const [slideoutOpen, setSlideoutOpen] = useState<boolean>(false);
  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(true);

  const [loading, setLoading] = useState<boolean>(true);

  const { questionnaire, questionnaireLoading } =
    useGetQuestionnaire(questionnaireId);

  const { survey, surveyLoading, surveyRefetch } = useGetSurvey({
    id: surveyId || 0,
  });
  const [updateSurvey] = useUpdateSurveyMutation();
  const [calculate] = useCalculateSurveyRecommendationsMutation();
  const [evaluate] = useEvaluateQuestionnaireMutation();

  const { data: project } = useGetProjectByIdQuery({
    slug: accountSlug,
    id: Number(project_id),
    include: ["rate-table"],
  });

  const { data: account, isLoading: accountLoading } =
    useGetAccount(accountSlug);

  const rateTableId = project?.data?.relationships?.["rate-table"]?.data?.id;
  const resourceRatesEnabled =
    account?.attributes?.settings?.project_resource_rates;
  const { resourceRates } = useListResourceRates(rateTableId);
  const { resources } = useListResources();
  const { projectResources } = useListProjectResources(project_id);

  useEffect(() => {
    if (!questionnaireLoading) {
      if (questionnaire?.questions && !questions)
        setQuestions(questionnaire.questions);

      if (questionnaire?.sections && !sections)
        setSections(questionnaire.sections);
    }
  }, [questionnaireLoading]);

  useEffect(() => {
    if (account?.id && questionnaire?.id && authorizationCode) {
      newSurvey({
        questionnaire: {
          id: questionnaireId,
          type: "questionnaires",
          attributes: { name: questionnaire?.name || "" },
        },
        account: {
          id: account?.attributes?.accountId,
          slug: accountSlug,
          authorizationCode,
        },
        projectId: project_id,
        surveyId: undefined,
        responses: undefined,
      }).then(async (res) => {
        const id = res?.data?.id;
        if (id) {
          setSurveyId(id);
          await surveyRefetch();
          setLoading(false);
        }
      });
    }
  }, [accountLoading, questionnaireLoading, authorizationCode]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (unsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [unsavedChanges]);

  useEffect(() => {
    if (shouldScrollToBottom && newSectionRef.current) {
      const rect = newSectionRef.current.getBoundingClientRect();
      const scrollOffset = rect.top + window.scrollY;

      // Viewport height adjustment (e.g. 75% down)
      const viewportAdjustment = window.innerHeight * 0.75;

      window.scrollTo({
        top: scrollOffset - viewportAdjustment,
        behavior: "smooth",
      });
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom, sections]);

  const handleFailResponse = (msg) => {
    setErrorMessage(msg || "Something went wrong");
    setShowFailAlert(true);
    setLoading(false);
  };

  const handleCalculate = async () => {
    if (!surveyId || !questionnaireId) return;

    const missingRequiredQuestion = questions?.some((q) => {
      const section = sections?.find(
        (s) => s.id === q["questionnaire-section-id"]
      );

      // If the section doesn't exist, skip the question
      if (!section) return false;

      const isSectionVisible =
        !section.expression ||
        (section.expression && section.show) ||
        !releaseSurveyAdaptiveQuestions;

      if (!isSectionVisible) return false;

      return (
        q.required &&
        !q.answer &&
        !q?.["select-options"]?.find((o) => o.default)
      );
    });

    if (missingRequiredQuestion) {
      handleFailResponse("Please complete all required fields");
      return;
    }

    //flips to true if at least one hidden section evaluates to true
    let showNewSection = false;

    if (releaseSurveyAdaptiveQuestions) {
      const updated = await Promise.all(
        sections?.map(async (section) => {
          //only execute for sections that have an expression declared
          if (!section.expression) return section;

          const data = {
            id: Number(questionnaireId),
            type: "questionnaires" as "questionnaires",
            attributes: {
              responses:
                questions?.map((q) => {
                  return {
                    question_id: Number(q?.["question-id"] || 0),
                    question: q.question || "",
                    answer: q.answer || "",
                  };
                }) || [],
              equation: section.expression || "",
            },
          };

          const evaluation = await evaluate({
            slug: accountSlug,
            id: Number(questionnaireId),
            body: { data },
          });

          //@ts-ignore
          const result = evaluation?.data?.data?.attributes?.result;

          //If any section is currently not showing, but its expression evaluates to true, trigger early return
          if (!section.show && result) showNewSection = true;

          return {
            ...section,
            //@ts-ignore
            show: !!result,
          };
        }) || []
      );
      setSections(updated);
    }

    // Do not proceed to calculate if a new section is shown
    if (showNewSection) {
      setShouldScrollToBottom(true);
      return;
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    setLoading(true);

    // Patch survey.responses
    const responses = questions?.map((q) => {
      const defaultValue = q?.["select-options"]?.find((o) => o.default);
      return {
        "question-id": q?.["question-id"],
        question: q?.question,
        //@ts-ignore
        answer: q?.answer || defaultValue?.value,
        "survey-response-id": q?.["survey-response-id"],
      };
    });

    try {
      const data: V1SurveyResource = {
        id: Number(surveyId),
        type: "surveys",
        attributes: {
          name: survey?.name || "",
          responses,
        },
        relationships: {
          questionnaire: {
            data: { id: Number(questionnaireId), type: "questionnaires" },
          },
        },
      };

      const updatedSurvey = (await updateSurvey({
        slug: accountSlug,
        id: Number(surveyId),
        body: { data },
      })) as UpdateSurveyApiResponse;

      // Calculate
      if (updatedSurvey?.data) {
        const updatedResponses =
          //@ts-ignore
          updatedSurvey?.data?.data?.attributes?.responses?.map((r) => {
            const question = questions?.find(
              (q) => String(q["question-id"]) == String(r["question-id"])
            );
            return {
              ...question,
              "survey-response-id": r["survey-response-id"],
            };
          });

        if (updatedResponses) setQuestions(updatedResponses);

        const calculated = (await calculate({
          slug: accountSlug,
          id: Number(surveyId),
        })) as CalculateSurveyRecommendationsApiResponse;

        if (calculated?.data) {
          const updated =
            //@ts-ignore
            calculated.data?.data?.attributes;

          const formattedRecommendations: Survey["recommendations"] =
            await formatRecommendations(
              updated?.recommendations || [],
              projectResources,
              authorizationCode,
              accountSlug,
              handleFailResponse,
              resourceRates,
              resourceRatesEnabled
            );

          setRecommendations(formattedRecommendations);
          setNonServiceRecommendations(
            updated?.recommendations?.filter(
              (r) => r["item_type"] !== "Task"
            ) || []
          );
          setStep(2);
          setLoading(false);
          surveyRefetch();
        }

        //@ts-ignore
        const error = calculated?.error?.data?.errors?.[0]?.detail;
        if (error) handleFailResponse(error);
      }

      //@ts-ignore
      const error = updatedSurvey?.error?.data?.errors?.[0]?.detail;
      if (error) handleFailResponse(error);
    } catch (err) {
      handleFailResponse("");
    }
  };

  const reviewColumns = [
    {
      id: "service name",
      header: () => "Service Name",
      cell(item) {
        return item.id !== "noRow" ? (
          <div className="serviceName">
            <Form.Control type="text" value={item.name} disabled={true} />
            {item.type === "services" && (
              <div className="previewService review">
                <FontAwesomeIcon
                  icon={faEye}
                  onClick={() => {
                    setServiceToPreview(item);
                    setSlideoutOpen(true);
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          item.name
        );
      },
    },
    {
      id: "total hours",
      header: () => "Total Hours",
      cell(item) {
        return (
          item.id !== "noRow" && (
            <Form.Control
              type="text"
              value={item.hours || ""}
              disabled={true}
            />
          )
        );
      },
    },
    {
      id: "qty",
      header: () => "QTY",
      cell(item) {
        return (
          item.id !== "noRow" && (
            <Form.Control
              type="number"
              min="0"
              step="1"
              defaultValue={item.quantity || 1}
              onBlur={(e) => {
                item = {
                  ...item,
                  quantity: e.target.value,
                };
                const updated = (recommendations || []).map((s) => {
                  if (item.type === "subservices") {
                    return {
                      ...s,
                      refinements:
                        s.refinements?.map((ref) =>
                          ref.id === item.id
                            ? { ...item, isChanged: true }
                            : ref
                        ) || [],
                    };
                  }

                  return s.id === item.id ? item : s;
                });
                setRecommendations(updated);
              }}
            />
          )
        );
      },
    },
    {
      id: "resource",
      header: () => "Resource",
      cell(item) {
        const service =
          item?.type === "subservices"
            ? recommendations?.find((s) => s.id === item?.serviceId)
            : undefined;

        //Use the defined resource if it exists, otherwise, if it is a subservice, default to service resource
        const resource =
          item?.resource || service?.resource
            ? {
                label: item?.resource?.name || service?.resource?.name,
                value: item?.resource?.id || service?.resource?.id,
              }
            : undefined;

        return (
          item?.id !== "noRow" &&
          item?.serviceType == "professional_services" && (
            <Select
              className="resourceDropdown"
              value={resource}
              options={renderResourceOptions(
                projectResources,
                resourceRatesEnabled ? resourceRates : resources,
                [],
                account?.attributes?.fieldLabels?.["vendor-resource"] ||
                  "Vendor Resource"
              )}
              onChange={(e) => {
                if (e) {
                  item = {
                    ...item,
                    resource: { id: e.value, name: e.label },
                  };
                  const updated = (recommendations || []).map((rec) => {
                    if (item.type === "subservices") {
                      return {
                        ...rec,
                        refinements:
                          rec.refinements?.map((ref) =>
                            ref.id === item.id
                              ? { ...item, isChanged: true }
                              : ref
                          ) || [],
                      };
                    }

                    return rec.id === item.id ? item : rec;
                  });
                  setRecommendations(updated);
                }
              }}
            />
          )
        );
      },
    },
  ];

  const showSpinner =
    loading ||
    accountLoading ||
    surveyLoading ||
    questionnaireLoading ||
    !questions ||
    !questionnaire ||
    !surveyId;

  return (
    <div className="servicesV2 applySurvey">
      {showSpinner ? (
        <ScopeStackSpinner />
      ) : (
        <>
          {" "}
          {handleAlert(
            showFailAlert,
            errorMessage,
            setShowFailAlert,
            "warning",
            ToastAlert
          )}
          <div className="actionBtns">
            <div className="leftHeader">
              {" "}
              <Button
                className="button cancelButton"
                onClick={(e) => {
                  if (step === 1) {
                    window.location.replace(
                      `${appHost}/projects/${project_id}/project_surveys`
                    );
                  } else {
                    setRecommendations(null);
                    setStep(1);
                  }
                }}
              >
                Back
              </Button>
              <div className="servicesH1">
                Add Services from {questionnaire?.name || "Survey"}
              </div>
            </div>
            <Button
              className={`button`}
              onClick={() => {
                if (step === 1) {
                  handleCalculate();
                } else {
                  setUnsavedChanges(false);
                  handleApply(
                    recommendations!,
                    nonServiceRecommendations,
                    surveyId,
                    project_id,
                    { slug: accountSlug, code: authorizationCode },
                    handleFailResponse,
                    fromProjectServices
                  );
                }
              }}
              disabled={!questions?.length || showSpinner}
            >
              {step === 1 ? "Continue" : "Apply to Project"}
            </Button>
          </div>
          <div className="servicesH2">
            {step === 1
              ? "1. Complete survey questionnaire."
              : "2. Review & apply recommendations."}
          </div>
          {step === 1 ? (
            <QuestionnaireForm
              questionnaire={questionnaire}
              questions={questions}
              setQuestions={setQuestions}
              sectionsFlag={releaseSurveyAdaptiveQuestions}
              sections={sections || []}
              newSectionRef={newSectionRef}
            />
          ) : (
            <SurveyRecommendations
              columns={reviewColumns}
              recommendations={recommendations || []}
              nonServiceRecommendations={nonServiceRecommendations}
              calculations={survey?.calculations || []}
              responses={survey?.responses || []}
            />
          )}
          <ServicePreview
            service={serviceToPreview}
            slideoutOpen={slideoutOpen}
            setSlideoutOpen={setSlideoutOpen}
            isManagedService={
              serviceToPreview?.serviceType === "managed_services"
            }
          />
        </>
      )}
    </div>
  );
};

export default New;
